// src/pages/courses/CourseLandingPage.tsx
"use client"
import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import VideoShowPlayer from "../../components/common/VideoShowPlayer"
import StatusUpdatePopup from "../../components/common/StatusUpdatePopup"
import { getCourseById, getCourseAdditionalByCourseId, getCoursePriceByCourseId, getCourseModulesByCourseId, getLessonsByModuleId, updateCourseStatus } from "../../api/courseService"
import { ArrowLeft, CheckCircle, ChevronDown, Play, Users, ImageIcon, File, HelpCircle } from "lucide-react"
import { formatSeconds } from '../../utils/time'
import NotFoundPage from "../../components/common/NotFoundPage";

const CourseLandingPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [course, setCourse] = useState<any>(null)
  const [, setCoursePrice] = useState<any>(null)
  const [selectedPriceOption, setSelectedPriceOption] = useState<any>(null)
  const [priceOptions, setPriceOptions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [priceLoading, setPriceLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [showVideoPlayer, setShowVideoPlayer] = useState(false)
  const [mediaType, setMediaType] = useState<"image" | "video" | "unknown">("unknown")
  const [activeFaq, setActiveFaq] = useState<number | null>(null)
  const [faqs, setFaqs] = useState<{question: string, answer: string}[]>([])
  const [loadingFaqs, setLoadingFaqs] = useState(true)
  // const [modules, setModules] = useState<any[]>([])
  const [loadingModules, setLoadingModules] = useState(true)
  const [modulesWithLessons, setModulesWithLessons] = useState<any[]>([])
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set())
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [, setIsUpdatingStatus] = useState(false)
  
  // Function to format validity period
  const formatValidityPeriod = (validityType: string, validityInDays: number) => {
    switch (validityType) {
      case '1month':
        return '1 month'
      case '3months':
        return '3 months'
      case '6months':
        return '6 months'
      case '1year':
        return '1 year'
      case 'custom':
        return `${validityInDays} days`
      default:
        return `${validityInDays} days`
    }
  }
  
  // Function to handle status update
  const handleStatusUpdate = async (courseId: string, newStatus: string) => {
    setIsUpdatingStatus(true)
    try {
      const response = await updateCourseStatus(courseId, newStatus, localStorage.getItem("USER_ID") || "")
      
      if (response.status === 200) {
        setCourse((prev: any) => ({ ...prev, status: newStatus }))
        alert('Course status updated successfully!')
      } else {
        alert(`Failed to update status: ${response.message}`)
      }
    } catch (error) {
      console.error('Error updating status:', error)
      alert('An error occurred while updating the status')
    } finally {
      setIsUpdatingStatus(false)
      setShowStatusPopup(false)
    }
  }
  
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true)
        const response = await getCourseById(id!)
        console.log("Course API Response:", response)
        if (response.status === 200 && response.data) {
          const courseData = response.data
          
          // Check if course is published - if not, show 404 for ALL users
          const isPublished = courseData.PublishStatus === "Published"
          
          // ONLY show content if it's published, regardless of user type
          if (!isPublished) {
            setError("Course not found")
            return
          }
          
          let thumbnail = courseData.thumbnail
          const videoUrl = courseData.video_url || courseData.thumbnail
          let detectedMediaType: "image" | "video" | "unknown" = "unknown"
          
          // Detect media type based on videoUrl
          if (videoUrl) {
            if (videoUrl.includes("/stream/") || videoUrl.includes(".m3u8") || videoUrl.includes(".mp4")) {
              detectedMediaType = "video"
              console.log("Video URL detected:", videoUrl)
            } else if (
              videoUrl.startsWith("data:image") ||
              videoUrl.includes(".jpg") ||
              videoUrl.includes(".jpeg") ||
              videoUrl.includes(".png") ||
              videoUrl.includes(".gif")
            ) {
              detectedMediaType = "image"
           //   console.log("Image URL detected:", videoUrl)
            } else if (!videoUrl.startsWith("http")) {
              // Base64 handling
              if (!thumbnail.startsWith("data:image")) {
                thumbnail = `data:image/jpeg;base64,${thumbnail}`
              }
              detectedMediaType = "image"
              console.log("Base64 image detected")
            } else {
              detectedMediaType = "image"
              console.log("Unknown media type, treating as image")
            }
          }
          
          setMediaType(detectedMediaType)
          setCourse({
            id: courseData._id,
            title: courseData.title,
            description: courseData.description || "No description provided",
            instructor: "Dr. Rakesh Bansal",
            thumbnail: thumbnail,
            videoUrl: videoUrl,
            status: courseData.PublishStatus || "Available", // Use actual status from API
          })
        } else {
          setError(response.message || "Failed to load course details")
        }
      } catch (err) {
        console.error("Error fetching course:", err)
        setError("Please check your authentication and try again")
      } finally {
        setLoading(false)
      }
    }
    
    const fetchFaqs = async () => {
      try {
        setLoadingFaqs(true)
        const response = await getCourseAdditionalByCourseId(id!)
        console.log("FAQ API Response:", response)
        if (response.status === 200 && response.data && response.data.FAQ) {
          setFaqs(response.data.FAQ)
        } else {
          console.log("No FAQs found or error in response")
          setFaqs([])
        }
      } catch (err) {
        console.error("Error fetching FAQs:", err)
        setFaqs([])
      } finally {
        setLoadingFaqs(false)
      }
    }
    
    const fetchCoursePrice = async () => {
      try {
        setPriceLoading(true)
        const response = await getCoursePriceByCourseId(id!)
        console.log("Course Price API Response:", response)
        if (response.status === 200 && response.data) {
          const priceData = response.data
          setCoursePrice(priceData)
          
          // Set price options from the priceDetail array
          if (priceData.priceDetail && Array.isArray(priceData.priceDetail)) {
            setPriceOptions(priceData.priceDetail)
            
            // Set the first price option as selected by default
            if (priceData.priceDetail.length > 0) {
              setSelectedPriceOption(priceData.priceDetail[0])
            }
          }
        } else {
          console.log("No course price found")
        }
      } catch (err) {
        console.error("Error fetching course price:", err)
      } finally {
        setPriceLoading(false)
      }
    }
    
    const fetchCourseModules = async () => {
      try {
        setLoadingModules(true)
        const response = await getCourseModulesByCourseId(id!)
        console.log("Course Modules API Response:", response)
        if (response.status === 200 && response.data) {
          // Fetch lessons for each module
          const modulesData = response.data
          const modulesWithLessonsData = await Promise.all(
            modulesData.map(async (module: any) => {
              try {
                const lessonsResponse = await getLessonsByModuleId(module._id)
                if (lessonsResponse.status === 200 && lessonsResponse.data) {
                  return {
                    ...module,
                    lessons: lessonsResponse.data
                  }
                } else {
                  return {
                    ...module,
                    lessons: []
                  }
                }
              } catch (error) {
                console.error(`Error fetching lessons for module ${module._id}:`, error)
                return {
                  ...module,
                  lessons: []
                }
              }
            })
          )
          setModulesWithLessons(modulesWithLessonsData)
        } else {
          console.log("No course modules found or error in response")
          setModulesWithLessons([])
        }
      } catch (err) {
        console.error("Error fetching course modules:", err)
        setModulesWithLessons([])
      } finally {
        setLoadingModules(false)
      }
    }
    
    if (id) {
      fetchCourse()
      fetchFaqs()
      fetchCoursePrice()
      fetchCourseModules()
    }
  }, [id])
  
  // Add this effect to update selectedPriceOption when priceOptions change
  useEffect(() => {
    if (priceOptions.length > 0 && !selectedPriceOption) {
      setSelectedPriceOption(priceOptions[0]);
    }
  }, [priceOptions, selectedPriceOption]);
  
  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription)
  }
  
  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index)
  }
  
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }
  
  const truncateDescription = (text: string, maxWords = 30) => {
    if (!text) return ""
    const words = text.split(" ")
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "..."
    }
    return text
  }
  
  // Get auth token for URLs
  const authToken = localStorage.getItem("AUTH_TOKEN")
  const videoSrc = course?.videoUrl
    ? course.videoUrl.startsWith("http")
      ? `${course.videoUrl}?token=${authToken}`
      : course.videoUrl
    : ""
  const thumbnailSrc = course?.thumbnail
    ? course.thumbnail.startsWith("http")
      ? `${course.thumbnail}?token=${authToken}`
      : course.thumbnail
    : ""
  
  // Function to handle closing video player
  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false)
  }
  
  // Function to handle buy now
  const handleBuyNow = () => {
    // Navigate to signup page
    navigate("/signup")
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }
  
  if (error) {
    return (
      <NotFoundPage
        title="Course Coming Soon!"
        subtitle="Course Not Available"
        message="The course you're looking for is currently unavailable. It might be in development or preparing for launch. Please check back later or explore our other exciting courses!"
        buttonText="Back to Login"
        gradientFrom="from-blue-500"
        gradientTo="to-indigo-600"
        iconColor="bg-blue-500"
      />
    );
  }
  
  if (!course) {
    return (
      <NotFoundPage
        title="Course Coming Soon!"
        subtitle="Course Not Available"
        message="The course you're looking for is currently unavailable. It might be in development or preparing for launch. Please check back later or explore our other exciting courses!"
        buttonText="Back to Login"
        gradientFrom="from-blue-500"
        gradientTo="to-indigo-600"
        iconColor="bg-blue-500"
      />
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-200/20 to-cyan-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-purple-100/10 to-blue-100/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Back Button */}
        <button
          onClick={() => navigate("/courses")}
          className="flex items-center text-gray-700 hover:text-blue-600 mb-8 transition-all duration-300 group bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm hover:shadow-md border border-white/50"
        >
          <ArrowLeft size={18} className="mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="font-medium text-sm">Back to courses</span>
        </button>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Course Title Header */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">{course.title}</h1>
                  <p className="text-sm md:text-base text-gray-600 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    by {course.instructor}
                  </p>
                </div>
                {/* Status badge with edit functionality */}
                <button 
                  onClick={() => setShowStatusPopup(true)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border backdrop-blur-sm transition-all duration-300 transform hover:scale-105 ${
                    course.status === "Draft" ? "bg-blue-50/80 text-blue-700 border-blue-200/50 hover:bg-blue-100/80 shadow-sm hover:shadow-md" :
                    course.status === "Published" ? "bg-green-50/80 text-green-700 border-green-200/50 hover:bg-green-100/80 shadow-sm hover:shadow-md" :
                    course.status === "Sold out" ? "bg-red-50/80 text-red-700 border-red-200/50 hover:bg-red-100/80 shadow-sm hover:shadow-md" :
                    course.status === "Selling" ? "bg-emerald-50/80 text-emerald-700 border-emerald-200/50 hover:bg-emerald-100/80 shadow-sm hover:shadow-md" :
                    "bg-gray-50/80 text-gray-700 border-gray-200/50 hover:bg-gray-100/80 shadow-sm hover:shadow-md"
                  } cursor-pointer`}
                >
                  <div className={`w-2 h-2 rounded-full mr-2 animate-pulse ${
                    course.status === "Draft" ? "bg-blue-500" :
                    course.status === "Published" ? "bg-green-500" :
                    course.status === "Sold out" ? "bg-red-500" :
                    course.status === "Selling" ? "bg-emerald-500" :
                    "bg-gray-500"
                  }`}></div>
                  {course.status}
                  <ChevronDown className="ml-2 transition-transform duration-300" size={12} />
                </button>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105">
                  <Users className="w-3 h-3 mr-2" />
                  Expert Level
                </span>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-green-50 to-green-100 text-green-700 border border-green-200/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105">
                  <CheckCircle className="w-3 h-3 mr-2" />
                  Lifetime Access
                </span>
              </div>
            </div>
            
            {/* About Course */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full mr-3"></div>
                About this course
              </h2>
              <div className="text-gray-700 leading-relaxed">
                {showFullDescription ? (
                  <p className="mb-3 text-sm">{course.description}</p>
                ) : (
                  <p className="mb-3 text-sm">{truncateDescription(course.description)}</p>
                )}
                <button
                  onClick={toggleDescription}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-all duration-300 bg-blue-50/50 hover:bg-blue-100/50 px-3 py-1 rounded-full border border-blue-200/50 backdrop-blur-sm"
                >
                  {showFullDescription ? "Show Less" : "Show More"}
                </button>
              </div>
            </div>
            
            {/* Offerings */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3"></div>
                What you'll get ({modulesWithLessons.length} {modulesWithLessons.length === 1 ? 'offering' : 'offerings'})
              </h3>
              {loadingModules ? (
                <div className="flex justify-center items-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : modulesWithLessons.length > 0 ? (
                <div className="space-y-2.5">
                  {modulesWithLessons.map((module, index) => {
                    const isExpanded = expandedModules.has(module._id)
                    
                    // Determine color based on index for variety
                    const colorClasses = [
                      'bg-blue-50 border-blue-100 text-blue-800',
                      'bg-green-50 border-green-100 text-green-800',
                      'bg-purple-50 border-purple-100 text-purple-800',
                      'bg-yellow-50 border-yellow-100 text-yellow-800',
                      'bg-pink-50 border-pink-100 text-pink-800'
                    ][index % 5]
                    
                    const iconColorClasses = [
                      'bg-blue-500',
                      'bg-green-500',
                      'bg-purple-500',
                      'bg-yellow-500',
                      'bg-pink-500'
                    ][index % 5]
                    
                    return (
                      <div key={module._id} className="space-y-2.5">
                        <div 
                          className={`flex items-center p-3 rounded-lg border ${colorClasses} cursor-pointer hover:opacity-90 transition-opacity`}
                          onClick={() => toggleModule(module._id)}
                        >
                          <div className={`w-8 h-8 ${iconColorClasses} rounded-md mr-2.5 flex items-center justify-center`}>
                            <Play className="text-white" size={14} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{module.title}</p>
                            <p className="text-xs text-gray-600 truncate">
                              {module.description || "No description available"}
                            </p>
                          </div>
                          <div className="ml-2 flex-shrink-0">
                            <ChevronDown className={`text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} size={16} />
                          </div>
                        </div>
                        
                        {/* Lessons for this module - only shown when expanded */}
                        {isExpanded && module.lessons && module.lessons.length > 0 && (
                          <div className="ml-3.5 pl-5 border-l-2 border-gray-200 space-y-2.5">
                            {module.lessons.map((lesson: any) => (
                              <div key={lesson._id} className="flex items-start p-2.5 bg-white rounded-lg border border-gray-200 hover:shadow-xs transition-shadow">
                                {/* Lesson type indicator */}
                                <div className="flex-shrink-0 mr-2.5">
                                  {lesson.lessonType === 'video' ? (
                                    <div className="relative aspect-[16/9] w-14 h-10 rounded-md overflow-hidden">
                                      {lesson.lessonThumbnail ? (
                                        <img 
                                          src={lesson.lessonThumbnail} 
                                          alt={lesson.title} 
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            const target = e.target as HTMLImageElement
                                            target.src = "https://via.placeholder.com/56x38/f3f4f6/6b7280?text=Video"
                                          }}
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                          <Play className="text-gray-400" size={14} />
                                        </div>
                                      )}
                                      {/* Play icon overlay */}
                                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                        <Play className="text-white" size={14} />
                                      </div>
                                    </div>
                                  ) : lesson.lessonType === 'document' ? (
                                    <div className="w-14 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                                      <File className="text-blue-600" size={16} />
                                    </div>
                                  ) : lesson.lessonType === 'quiz' ? (
                                    <div className="w-14 h-10 rounded-md bg-purple-50 flex items-center justify-center">
                                      <HelpCircle className="text-purple-600" size={16} />
                                    </div>
                                  ) : (
                                    <div className="w-14 h-10 rounded-md bg-gray-100 flex items-center justify-center">
                                      <Play className="text-gray-400" size={16} />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 text-xs truncate">{lesson.title}</h4>
                                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                    {lesson.description || "No description available"}
                                  </p>
                                  {/* Lesson type indicator with icon */}
                                  <div className="mt-1">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100">
                                      {lesson.lessonType === 'video' ? (
                                        <>
                                          <Play className="w-2.5 h-2.5 mr-1" /> Video
                                        </>
                                      ) : lesson.lessonType === 'document' ? (
                                        <>
                                          <File className="w-2.5 h-2.5 mr-1" /> Document
                                        </>
                                      ) : lesson.lessonType === 'quiz' ? (
                                        <>
                                          <HelpCircle className="w-2.5 h-2.5 mr-1" /> Quiz
                                        </>
                                      ) : (
                                        <>
                                          <Play className="w-2.5 h-2.5 mr-1" /> Lesson
                                        </>
                                      )}
                                      {lesson.duration ? ` • ${formatSeconds(lesson.duration)}` : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Message when expanded but no lessons */}
                        {isExpanded && module.lessons && module.lessons.length === 0 && (
                          <div className="ml-3.5 pl-5 border-l-2 border-gray-200 py-2.5">
                            <p className="text-gray-500 text-xs">No lessons available for this module.</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">No offerings available for this course.</p>
                </div>
              )}
            </div>
            
            {/* FAQs */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full mr-3"></div>
                Frequently Asked Questions
              </h3>
              {loadingFaqs ? (
                <div className="flex justify-center items-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : faqs.length > 0 ? (
                <div className="space-y-2.5">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg">
                      <button
                        className="flex justify-between items-center w-full text-left p-3 hover:bg-gray-50 transition-colors"
                        onClick={() => toggleFaq(index)}
                      >
                        <span className="font-medium text-gray-900 pr-3 text-sm">{faq.question}</span>
                        <ChevronDown className={`text-gray-500 transition-transform flex-shrink-0 ${
                          activeFaq === index ? 'rotate-180' : ''
                        }`} size={16} />
                      </button>
                      {activeFaq === index && (
                        <div className="px-3 pb-3 border-t border-gray-200">
                          <p className="text-gray-700 leading-relaxed pt-2.5 text-sm">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">No FAQs available for this course.</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Compact Cart Sidebar - Only show if status is Published */}
          {course.status === "Published" && (
            <div className="lg:col-span-1">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl sticky top-6 overflow-hidden transform hover:scale-[1.02] transition-all duration-300">
                {/* Video Preview */}
                <div className="p-3.5">
                  <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {showVideoPlayer && videoSrc ? (
                      <VideoShowPlayer src={videoSrc} onClose={handleCloseVideoPlayer} autoPlay={true} />
                    ) : mediaType === "image" ? (
                      <img
                        src={thumbnailSrc}
                        alt={course.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = "https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Course+Preview"
                        }}
                      />
                    ) : mediaType === "video" ? (
                      <>
                        <img
                          src={thumbnailSrc}
                          alt={course.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.src = "https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Video+Preview"
                          }}
                        />
                        <button
                          onClick={() => setShowVideoPlayer(true)}
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-20 transition-all group"
                        >
                          <div className="bg-white rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform">
                            <Play className="text-blue-600 ml-0.5" size={16} />
                          </div>
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <ImageIcon size={24} className="mx-auto mb-1.5 text-gray-400" />
                          <span className="text-xs font-medium">Course Preview</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Pricing Section - Only show if status is Published */}
                <div className="bg-gradient-to-br from-gray-50/80 to-blue-50/80 backdrop-blur-sm px-4 py-4 border-t border-white/50">
                  {priceLoading ? (
                    <div className="flex justify-center items-center py-3.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : priceOptions.length > 0 ? (
                    <div className="space-y-3">
                      {selectedPriceOption && (
                        <div className="text-center">
                          <div className="text-xl font-bold text-gray-900">₹{selectedPriceOption.actualPrice}</div>
                          {selectedPriceOption.price && selectedPriceOption.price !== selectedPriceOption.actualPrice && (
                            <div className="text-xs text-gray-500 line-through">₹{selectedPriceOption.price}</div>
                          )}
                          {selectedPriceOption.validityInDays && (
                            <div className="text-xs text-gray-600 mt-1">
                              Valid for {formatValidityPeriod(selectedPriceOption.validityType, selectedPriceOption.validityInDays)}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {priceOptions.length > 1 && (
                        <div className="mt-2.5">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Other options:</label>
                          <select
                            value={selectedPriceOption?._id || ''}
                            onChange={(e) => {
                              const selected = priceOptions.find(option => option._id === e.target.value)
                              if (selected) {
                                setSelectedPriceOption(selected)
                              }
                            }}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            {priceOptions.map((option) => (
                              <option key={option._id} value={option._id}>
                                {`₹${option.actualPrice} - ${formatValidityPeriod(option.validityType, option.validityInDays)}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      <button
                        onClick={handleBuyNow}
                        className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-lg shadow-md"
                      >
                        🛒 Buy Now
                      </button>
                    </div>
                  ) : (
                    <div className="text-center space-y-2.5">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                        <div className="text-red-600 font-semibold text-xs">❌ Sold Out</div>
                        <p className="text-xs text-red-500 mt-1">
                          Currently unavailable
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Show status message when course is not Published */}
          {course.status !== "Published" && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg sticky top-6 overflow-hidden">
                {/* Video Preview */}
                <div className="p-3.5">
                  <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {showVideoPlayer && videoSrc ? (
                      <VideoShowPlayer src={videoSrc} onClose={handleCloseVideoPlayer} autoPlay={true} />
                    ) : mediaType === "image" ? (
                      <img
                        src={thumbnailSrc}
                        alt={course.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = "https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Course+Preview"
                        }}
                      />
                    ) : mediaType === "video" ? (
                      <>
                        <img
                          src={thumbnailSrc}
                          alt={course.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.src = "https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Video+Preview"
                          }}
                        />
                        <button
                          onClick={() => setShowVideoPlayer(true)}
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-20 transition-all group"
                        >
                          <div className="bg-white rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform">
                            <Play className="text-blue-600 ml-0.5" size={16} />
                          </div>
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <ImageIcon size={24} className="mx-auto mb-1.5 text-gray-400" />
                          <span className="text-xs font-medium">Course Preview</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Status Message */}
                <div className="bg-gray-50 px-3.5 py-3.5 border-t border-gray-200">
                  <div className="text-center space-y-2.5">
                    <div className={`rounded-lg p-2.5 ${
                      course.status === "Draft" ? "bg-blue-50 border border-blue-200" :
                      course.status === "Sold out" ? "bg-red-50 border border-red-200" :
                      "bg-gray-50 border border-gray-200"
                    }`}>
                      <div className={`font-semibold text-xs ${
                        course.status === "Draft" ? "text-blue-600" :
                        course.status === "Sold out" ? "text-red-600" :
                        "text-gray-600"
                      }`}>
                        {course.status === "Draft" ? "📝 Draft" :
                         course.status === "Sold out" ? "❌ Sold Out" :
                         `Status: ${course.status}`}
                      </div>
                      <p className={`text-xs mt-1 ${
                        course.status === "Draft" ? "text-blue-500" :
                        course.status === "Sold out" ? "text-red-500" :
                        "text-gray-500"
                      }`}>
                        {course.status === "Draft" ? "This course is currently in draft mode and not available for purchase." :
                         course.status === "Sold out" ? "This course is currently sold out and unavailable for purchase." :
                         `This course is currently in ${course.status.toLowerCase()} mode and not available for purchase.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Status Update Popup */}
      <StatusUpdatePopup
        isOpen={showStatusPopup}
        onClose={() => setShowStatusPopup(false)}
        currentStatus={course.status}
        courseId={course.id}
        onUpdate={handleStatusUpdate}
      />
    </div>
  )
}

export default CourseLandingPage